import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getPlayerById, Player } from '../api/players.api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

const PlayerProfilePage: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const { user } = useAuth();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  
  const isOwnProfile = user && player && user.id === player.id;

  useEffect(() => {
    const fetchPlayer = async () => {
      if (!playerId) {
        setLoading(false);
        return;
      }
      try {
        const response = await getPlayerById(playerId);
        setPlayer(response.data);
      } catch (err) {
        // Silent fail for prototype
      } finally {
        setLoading(false);
      }
    };
    fetchPlayer();
  }, [playerId]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!player) {
    return (
      <div className="container py-5">
        <div className="text-center py-5">
          <h2 className="mb-3">Player not found</h2>
          <Link to="/players" className="btn btn-outline-secondary">
            Back to Players
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <p className="player-profile-section-title mb-2">Player Profile</p>
          <h1 className="h2 fw-bold mb-0">{player.name}</h1>
        </div>
        <div className="d-flex gap-2">
          {isOwnProfile && (
            <Link to="/dashboard/profile/edit" className="btn btn-arena">
              <i className="bi bi-pencil me-1"></i>
              Edit Profile
            </Link>
          )}
          <Link to="/players" className="btn btn-outline-secondary">
            Back to Players
          </Link>
        </div>
      </div>

      <div className="card card-surface player-profile-hero mb-4">
        <div className="card-body">
          <div className="row align-items-center g-4">
            <div className="col-12 col-md-4 text-center text-md-start">
              {player.thumbnail ? (
                <img
                  src={player.thumbnail}
                  alt={player.name}
                  className="player-profile-avatar rounded-circle"
                />
              ) : (
                <div className="player-profile-avatar rounded-circle bg-dark d-flex align-items-center justify-content-center text-white fs-1">
                  {player.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="col-12 col-md-8">
              <div className="player-profile-meta d-flex flex-wrap gap-2 mb-3">
                <span className="badge bg-warning text-white">Role: {player.role}</span>
                <span className="badge bg-light text-white border">District: {player.district}</span>
              </div>
              <p className="text-muted-soft mb-0">{player.bio}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-7">
          <div className="card card-surface h-100">
            <div className="card-body">
              <p className="player-profile-section-title mb-3">Basic Details</p>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="player-profile-detail">
                    <div className="text-muted-soft small">Player ID</div>
                    {player.playerId ? (
                      <div className="fw-semibold text-success">{player.playerId}</div>
                    ) : (
                      <div className="fw-semibold text-warning" title="Backend not returning player_id">Not Available</div>
                    )}
                  </div>
                </div>
                {player.age !== null && (
                  <div className="col-12 col-md-6">
                    <div className="player-profile-detail">
                      <div className="text-muted-soft small">Age</div>
                      <div className="fw-semibold">{player.age}</div>
                    </div>
                  </div>
                )}
                <div className="col-12 col-md-6">
                  <div className="player-profile-detail">
                    <div className="text-muted-soft small">Playing Role</div>
                    <div className="fw-semibold">{player.role}</div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="player-profile-detail">
                    <div className="text-muted-soft small">Batting Style</div>
                    <div className="fw-semibold">{player.battingStyle}</div>
                  </div>
                </div>
                {player.bowlingStyle && (
                  <div className="col-12 col-md-6">
                    <div className="player-profile-detail">
                      <div className="text-muted-soft small">Bowling Style</div>
                      <div className="fw-semibold">{player.bowlingStyle}</div>
                    </div>
                  </div>
                )}
                <div className="col-12 col-md-6">
                  <div className="player-profile-detail">
                    <div className="text-muted-soft small">District</div>
                    <div className="fw-semibold">{player.district}</div>
                  </div>
                </div>
                {player.jersey_size && (
                  <div className="col-12 col-md-6">
                    <div className="player-profile-detail">
                      <div className="text-muted-soft small">Jersey Size</div>
                      <div className="fw-semibold">{player.jersey_size}</div>
                    </div>
                  </div>
                )}
                {player.cricheroes_profile_link && (
                  <div className="col-12 col-md-6">
                    <div className="player-profile-detail">
                      <div className="text-muted-soft small">Cricheroes Profile</div>
                      <div>
                        <a
                          href={player.cricheroes_profile_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-warning"
                        >
                          View Profile <i className="bi bi-box-arrow-up-right ms-1"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-5">
          <div className="card card-surface h-100">
            <div className="card-body">
              <p className="player-profile-section-title mb-3">Player Bio</p>
              <p className="text-muted-soft mb-0">{player.bio}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Hire Information */}
      <div className="card card-surface mt-4">
        <div className="card-body text-center">
          <div className="text-danger fw-bold" style={{ fontSize: '1.2rem' }}>
            FOR HIRE THIS PLAYER<br />
            CALL TO- 01788-083425
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerProfilePage;
